/**
 * Synthetic per-variant performance card injection (RENDERED-ONLY).
 *
 * Promotes each variant's perf card to a REAL frontend-synthetic ELK node at
 * the head of that variant's lane — between the split step's per-variant port
 * and the variant path's first real step. ELK then:
 *   - centers each card in its lane (variant label sits under the lane's turn),
 *   - equalizes lane widths (cards are a fixed size), and
 *   - routes the real edges into/out of the card, so the (+) buttons become
 *     ordinary on-edge add buttons that line up across lanes.
 *
 * This mirrors the convergence-placeholder SIZING mechanism (fixed pre-measure
 * dimensions, see node-dimensions.ts) but — unlike convergence dots — these
 * nodes are NOT canonical. They are injected here, just before ELK runs, and
 * exist only in the rendered graph projection. They never appear in:
 *   - canonical reducer state (state.nodes / state.edges),
 *   - serialization (parseJourneyFromFlow),
 *   - validation / invariants,
 *   - dirty-detection / snapshot,
 *   - enrichment, or reducer mutations.
 *
 * The only place that knows about them is the render pipeline: this module,
 * node-dimensions (sizing), the perf-card node renderer, and the render-graph
 * reconcile guard in use-rendered-journey-graph (which skips nodes/edges with
 * no canonical counterpart).
 *
 * ONLY VARIANT_SPLIT sources get cards. Condition splits and linear journeys
 * are untouched.
 */
import { StepType } from '@pushly/models/lib/enums/journeys'
import { isVariantSplitStep } from '@pushly/models/lib/structs/journeys'
import {
    JourneyNode,
    JourneyEdge,
    JourneyStepData,
    PerfCardMeta,
} from '../types/journey-nodes'
import {
    PERF_CARD_NODE_TYPE,
    PERF_CARD_NODE_ID_PREFIX,
    PERF_CARD_EDGE_ID_PREFIX,
    BRANCH_HANDLE_PREFIX,
} from '../constants'

export function getPerfCardNodeId(splitId: string, branchKey: string): string {
    return `${PERF_CARD_NODE_ID_PREFIX}${splitId}::${branchKey}`
}

export function getPerfCardOutEdgeId(splitId: string, branchKey: string): string {
    return `${PERF_CARD_EDGE_ID_PREFIX}${splitId}::${branchKey}`
}

export function isPerfCardId(id: string): boolean {
    return id.startsWith(PERF_CARD_NODE_ID_PREFIX)
}

export function isPerfCardEdgeId(id: string): boolean {
    return id.startsWith(PERF_CARD_EDGE_ID_PREFIX)
}

/**
 * Build a synthetic perf-card node. It carries a minimal PLACEHOLDER-typed step
 * so the layout traversal (sortNodesForModelOrder / symmetrize spine walk) can
 * follow its single out_step_id to the variant's first real step. The
 * `perfCardOf` uiMeta marker is what node-dimensions + the renderer key off.
 */
function makePerfCardNode(
    cardId: string,
    outStepId: string,
    meta: PerfCardMeta,
): JourneyNode {
    return {
        id: cardId,
        type: PERF_CARD_NODE_TYPE,
        position: { x: 0, y: 0 },
        data: new JourneyStepData({
            step: {
                id: cardId,
                type: StepType.PLACEHOLDER,
                configuration: { out_step_id: outStepId },
                meta: {},
            } as any,
            uiMeta: { perfCardOf: meta },
        }),
    } as JourneyNode
}

/**
 * Inject synthetic per-variant perf-card nodes into a COPY of the rendered
 * graph. Returns new arrays — the inputs are never mutated.
 *
 * For each VARIANT_SPLIT node, for each variant branch:
 *   1. Insert a card node at the head of the lane.
 *   2. Retarget the branch edge (split port → original target) to the card.
 *   3. Add a card → original-target edge (type 'custom', so it gets the
 *      ordinary on-edge add button).
 *   4. Clone the split step so branch.out_step_id points at the card — this is
 *      what makes ELK + the symmetrize spine walk treat the card as the lane
 *      head. The clone is layout-only; canonical state is untouched.
 */
export function injectPerfCardNodes(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
): { nodes: JourneyNode[]; edges: JourneyEdge[] } {
    const splitNodes = nodes.filter(
        (n) => n.data?.step?.type === StepType.VARIANT_SPLIT && isVariantSplitStep(n.data.step),
    )
    if (splitNodes.length === 0) {
        return { nodes, edges }
    }

    // Index branch edges by (splitId, branchKey) using the source handle.
    const edgeBySplitBranch = new Map<string, JourneyEdge>()
    for (const edge of edges) {
        if (!edge.sourceHandle?.startsWith(BRANCH_HANDLE_PREFIX)) continue
        const branchKey = edge.sourceHandle.slice(BRANCH_HANDLE_PREFIX.length)
        edgeBySplitBranch.set(`${edge.source}::${branchKey}`, edge)
    }

    const nextNodes: JourneyNode[] = [...nodes]
    const newCardEdges: JourneyEdge[] = []
    // edges we are retargeting, keyed by edge id → new target card id
    const retargetById = new Map<string, string>()
    // Per split: cloned branch out_step_id rewiring → { branchKey: cardId }
    const rewireBySplit = new Map<string, Map<string, string>>()

    for (const splitNode of splitNodes) {
        const step = splitNode.data.step
        if (!isVariantSplitStep(step)) continue
        const branches = step.configuration.branches ?? []
        if (branches.length < 2) continue

        const rewire = new Map<string, string>()

        for (const branch of branches) {
            const branchKey = String(branch.key)
            const branchEdge = edgeBySplitBranch.get(`${splitNode.id}::${branchKey}`)
            // Original lane target = where the branch edge currently points
            // (first real step, or the join target for an empty variant).
            const originalTarget = branchEdge
                ? branchEdge.target
                : branch.out_step_id != null
                    ? String(branch.out_step_id)
                    : null
            if (!originalTarget) continue

            const cardId = getPerfCardNodeId(splitNode.id, branchKey)
            const meta: PerfCardMeta = {
                splitId: splitNode.id,
                branchKey,
                variantName: branch.label,
                percentage: branch.kind === 'variant' ? branch.percentage : undefined,
            }

            nextNodes.push(makePerfCardNode(cardId, originalTarget, meta))
            rewire.set(branchKey, cardId)

            // Retarget the branch edge to the card (preserves source handle + data).
            if (branchEdge) {
                retargetById.set(branchEdge.id, cardId)
            }

            // Card → original target. Ordinary custom edge → normal add button.
            newCardEdges.push({
                id: getPerfCardOutEdgeId(splitNode.id, branchKey),
                source: cardId,
                target: originalTarget,
                type: 'custom',
            } as JourneyEdge)
        }

        if (rewire.size > 0) {
            rewireBySplit.set(splitNode.id, rewire)
        }
    }

    // Apply split-step rewiring (layout-only clone) so the spine walk follows
    // the card. We rebuild affected split nodes with a cloned step.
    const finalNodes = nextNodes.map((node) => {
        const rewire = rewireBySplit.get(node.id)
        if (!rewire) return node
        const step = node.data.step
        if (!isVariantSplitStep(step)) return node

        const clonedBranches = (step.configuration.branches ?? []).map((b) => {
            const cardId = rewire.get(String(b.key))
            return cardId ? { ...b, out_step_id: cardId } : { ...b }
        })
        const clonedStep = {
            ...step,
            configuration: { ...step.configuration, branches: clonedBranches },
        }
        return {
            ...node,
            data: new JourneyStepData({
                step: clonedStep as any,
                uiMeta: node.data.uiMeta,
            }),
        } as JourneyNode
    })

    // Apply edge retargeting.
    const finalEdges = edges.map((edge) => {
        const newTarget = retargetById.get(edge.id)
        return newTarget ? { ...edge, target: newTarget } : edge
    })

    return {
        nodes: finalNodes,
        edges: [...finalEdges, ...newCardEdges],
    }
}
